import { cn } from "@/lib/utils";
import { Fragment, type ReactNode } from "react";

interface JsonPrettyProps {
  value: unknown;
  className?: string;
  /**
   * When true, render bare (no background/padding/height cap) so a parent
   * container controls sizing and scrolling. Defaults to a self-contained,
   * height-capped block.
   */
  fill?: boolean;
  /**
   * When set, render the JSON monochrome in the diff tone (removed = red,
   * added = green) instead of syntax-highlighted — matches how simple/plain
   * values are tinted in a version diff.
   */
  tone?: "added" | "removed";
}

// Matches JSON string literals (incl. object keys ending in `:`), booleans,
// null, and numbers so each can be colorized.
const TOKEN =
  /("(?:\\.|[^"\\])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

const classify = (token: string): string => {
  if (token.startsWith('"')) {
    // A key is a string immediately followed by a colon.
    return /:\s*$/.test(token) ? "text-secondary" : "text-success";
  }
  if (token === "true" || token === "false" || token === "null") {
    return "text-link";
  }
  return "text-warning"; // number
};

/** Split pretty-printed JSON into colorized React nodes (no HTML injection). */
const highlight = (json: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let index = 0;
  TOKEN.lastIndex = 0;
  for (let match = TOKEN.exec(json); match !== null; match = TOKEN.exec(json)) {
    if (match.index > lastIndex) {
      nodes.push(
        <Fragment key={index++}>{json.slice(lastIndex, match.index)}</Fragment>,
      );
    }
    const token = match[0];
    nodes.push(
      <span key={index++} className={classify(token)}>
        {token}
      </span>,
    );
    lastIndex = match.index + token.length;
  }
  if (lastIndex < json.length) {
    nodes.push(<Fragment key={index++}>{json.slice(lastIndex)}</Fragment>);
  }
  return nodes;
};

/**
 * Compact, syntax-highlighted JSON for nested FHIR values. Wraps (never
 * stretches the layout) and caps its height so large fields scroll internally.
 */
export const JsonPretty = ({
  value,
  className,
  fill,
  tone,
}: JsonPrettyProps) => {
  const json = JSON.stringify(value, null, 2);
  return (
    <pre
      className={cn(
        "whitespace-pre-wrap break-words font-mono text-body-xs leading-relaxed",
        !fill && "max-h-64 overflow-auto rounded-md bg-muted p-2",
        tone === "removed" && "text-error",
        tone === "added" && "text-success",
        className,
      )}
    >
      {/* Tone overrides syntax highlighting so a removed/added value reads
          as a single diff color, like the plain-value case. */}
      {tone ? json : highlight(json)}
    </pre>
  );
};
